import css from './ManageRegistrationForm.module.css';

import Validators from '../../../utility/validators';
import {
  DataError,
  DataStatus,
  ErrorCode,
  FormField,
  FormStatus,
  ManageRegistrationMode,
} from '../../../types/appTypes';
import { useForm } from '../../../hooks/useForm';
import { useData } from '../../../hooks/useData';
import {
  Company,
  KeyProfile,
  Project,
  Registration,
  UserProfileSearchResult,
} from '../../../types/appModels';
import DataForm from '../../Common/DataForm/DataForm';
import React, { useContext, useState } from 'react';
import { UserContext } from '../../../context/userContext';
import SelectInput from '../../Common/SelectInput/SelectInput';
import Visibility from '../../Common/Visibility/Visibility';
import CheckboxInput from '../../Common/CheckboxInput/CheckboxInput';
import TextInput from '../../Common/TextInput/TextInput';
import { FiInfo } from 'react-icons/fi';

type ManageRegistrationFormProps = {
  mode: ManageRegistrationMode;
  dataId?: string;
  profileId?: string;
  onSuccess?: () => void;
};

type ManageRegistrationData = {
  regId?: string;
  email: string;
  name: string;
  companyId: string;
  company: string;
  apiRequest: boolean;
  atlasRequest: boolean;
  role: string;
  userType: string; // Added userType
  apiType: string;
  apiData: string[];
  apiCompanyIds: string[];
  apiCompanies: string[];
};

type ManageRegistrationFormFields = {
  email: FormField<string>;
  name: FormField<string>;
  companyId: FormField<string>;
  company: FormField<string>;
  approveApi: FormField<boolean>;
  approveAtlas: FormField<boolean>;
  role: FormField<string>;
  userType: FormField<string>; // Added userType
  apiType: FormField<string>;
  apiData: FormField<string[]>;
  apiCompanyIds: FormField<string[]>;
  apiCompanies: FormField<string[]>;
  genNewKey: FormField<boolean>;
};

// Define tooltip content for user types
const userTypeTooltips = {
  'Lite': 'Restricted access (e.g. water transfer)',
  'User': 'Default access with regular permissions',
  'Ambassador': 'Upgraded privilege of managing company\'s users',
  'Manager': 'Management access',
  'Admin': 'Administrative access'
};

const ManageRegistrationForm = ({
  mode,
  dataId,
  profileId,
  onSuccess,
}: ManageRegistrationFormProps) => {
  const isCreate = mode === ManageRegistrationMode.Create;

  const { user } = useContext(UserContext);
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  
  const { fields, formState, setFields, setFormState } =
    useForm<ManageRegistrationFormFields>({
      initialFields: {
        email: { value: '', valid: false },
        name: { value: '', valid: false },
        companyId: { value: '', valid: false },
        company: { value: '', valid: false },
        approveApi: {
          value: isCreate ? true : false,
          valid: true,
        },
        approveAtlas: {
          value: isCreate ? true : false,
          valid: true,
        },
        role: { value: '', valid: false },
        userType: { value: '', valid: false }, // Added userType
        apiType: { value: '', valid: isCreate ? false : true },
        apiData: { value: [], valid: isCreate ? false : true },
        apiCompanyIds: { value: [], valid: isCreate ? false : true },
        apiCompanies: { value: [], valid: isCreate ? false : true },
        genNewKey: { value: false, valid: true },
      },
    });

  // Function to show tooltip on hover
  const handleTooltipShow = (userType: string) => {
    setShowTooltip(userType);
  };

  // Function to hide tooltip
  const handleTooltipHide = () => {
    setShowTooltip(null);
  };

  const { dataState: regDataState } = useGetDataState(mode, dataId, setFields);

  const { dataState: companyDataState } = useData<Company[]>({
    endpoint: `${process.env.REACT_APP_API_COMPANIES}?top=false`,
    initialData: [],
    initialStatus: DataStatus.Loading,
    loadWhen: dataId !== null && dataId !== '',
  });

  const { dataState: projectsDataState, reloadData: reloadProjects } = useData<
    Project[]
  >({
    endpoint: process.env.REACT_APP_API_PROJECT_SEARCH!,
    method: 'POST',
    initialData: [],
    initialStatus: DataStatus.Ready,
    loadWhen:
      dataId !== null &&
      dataId !== '' &&
      fields.apiCompanies.value.length > 0 &&
      fields.apiType.value === 'Pad',
    body: JSON.stringify({
      top: false,
      companies: fields.apiCompanies.value,
    }),
    onComplete: (data, status, error) => {
      if (fields.apiType.value === 'Pad' && data !== null) {
        const padNames = data!.map((project) => project.padName);
        const selectedNames = fields.apiData.value;
        let changed = false;
        for (let i = 0; i < selectedNames.length; i++) {
          const name = selectedNames[i];
          if (!padNames.includes(name)) {
            selectedNames.splice(i, 1);
            changed = true;
          }
        }

        if (changed) {
          setFields((pv) => {
            return {
              ...pv,
              apiData: {
                value: selectedNames,
                valid: selectedNames.length > 0,
              },
            };
          });
        }
      }
    },
  });

  const formIsValid =
    fields.email.valid &&
    fields.name.valid &&
    fields.companyId.valid &&
    fields.company.valid &&
    fields.role.valid &&
    fields.userType.valid && // Added userType check
    fields.apiType.valid &&
    fields.apiData.valid &&
    fields.apiCompanyIds.valid &&
    fields.apiCompanies.valid &&
    fields.genNewKey.valid;

  // Function to get user type options based on role
  const getUserTypeOptions = () => {
    if (fields.role.value === 'Admin') {
      return [{ key: 'Admin', label: 'Admin', value: 'Admin' }];
    } else if (fields.role.value === 'Manager') {
      return [{ key: 'Manager', label: 'Manager', value: 'Manager' }];
    } else if (fields.role.value === 'User') {
      return [
        { key: 'Lite', label: 'Lite', value: 'Lite' },
        { key: 'User', label: 'Default User', value: 'User' },
        { key: 'Ambassador', label: 'Ambassador', value: 'Ambassador' }
      ];
    }
    return [];
  };

  const companyChangeHandler = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const companyId = event.target.value;
    let company = '';
    if (
      event.target.selectedOptions !== null &&
      event.target.selectedOptions.length > 0
    )
      company = event.target.selectedOptions[0].label;

    const valid =
      Validators.required(companyId) && Validators.required(company);

    setFields((pv) => {
      let newState = {
        ...pv,
        companyId: { value: companyId, valid: valid },
        company: { value: company, valid: valid },
      };

      return newState;
    });
  };

  const apiTypeChangeHandler = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const apiType = event.target.value;
    const valid = Validators.required(apiType);

    setFields((pv) => {
      let newState = {
        ...pv,
        apiType: { value: apiType, valid: valid },
      };

      const apiCompaniesSet =
        fields.apiCompanyIds.value.length > 0 &&
        fields.apiCompanies.value.length > 0;

      if (apiType === 'Company') {
        newState = {
          ...newState,
          apiData: { value: fields.apiCompanies.value, valid: apiCompaniesSet },
        };
      } else {
        newState = {
          ...newState,
          apiData: { value: [], valid: false },
        };
        if (apiCompaniesSet) {
          reloadProjects();
        }
      }

      return newState;
    });
  };

  const apiCompanyChangeHandler = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { selectedOptions } = event.target;

    const companyIds: string[] = [];
    const companies: string[] = [];
    for (let i = 0; i < selectedOptions.length; i++) {
      const id = selectedOptions.item(i)?.value;
      const name = selectedOptions.item(i)?.label;

      if (Validators.required(id) && Validators.required(name)) {
        companyIds.push(id!);
        companies.push(name!);
      }
    }
    const valid =
      companyIds.length > 0 && companyIds.length === companies.length;

    setFields((pv) => {
      let newState = {
        ...pv,
        apiCompanyIds: { value: valid ? companyIds : [], valid: valid },
        apiCompanies: { value: valid ? companies : [], valid: valid },
      };

      if (fields.apiType.value === 'Company') {
        newState = {
          ...newState,
          apiData: { value: valid ? companies : [], valid: valid },
        };
      }

      if (fields.apiType.value === 'Pad') {
        reloadProjects();
      }

      return newState;
    });
  };

  const requiredSelectChangeHandler = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, multiple, selectedOptions } = event.target;

    let valid: boolean;
    const values: string[] = [];
    if (multiple) {
      valid = selectedOptions.length > 0;
      for (let i = 0; i < selectedOptions.length; i++) {
        const value = selectedOptions.item(i)?.value;
        if (value !== null && value !== '') values.push(value!);
        else valid = false;
      }
    } else {
      const value = selectedOptions.item(0)!.value;
      values.push(value);
      valid = Validators.required(value);
    }

    setFields((pv) => {
      let updatedFields = {
        ...pv,
        [name]: { value: multiple ? values : values[0], valid: valid },
      };

      // If role is changing, set default userType
      if (name === 'role') {
        const roleValue = values[0];
        if (roleValue === 'Manager' || roleValue === 'Admin') {
          updatedFields.userType = { value: roleValue, valid: true };
        } else if (roleValue === 'User') {
          updatedFields.userType = { value: 'User', valid: true };
        }
      }

      return updatedFields;
    });
  };

  const approveAtlasCheckboxChangeHandler = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, checked } = event.target;

    setFields((pv) => {
      return {
        ...pv,
        role: { value: '', valid: !checked },
        userType: { value: '', valid: !checked }, // Reset userType when atlas approval changes
        company: { value: '', valid: !checked },
        companyId: { value: '', valid: !checked },
        [name]: { value: checked, valid: true },
      };
    });
  };

  const approveApiCheckboxChangeHandler = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, checked } = event.target;

    setFields((pv) => {
      return {
        ...pv,
        apiData: { value: [], valid: !checked },
        apiType: { value: '', valid: !checked },
        apiCompanyIds: { value: [], valid: !checked },
        apiCompanies: { value: [], valid: !checked },
        [name]: { value: checked, valid: true },
      };
    });
  };

  const checkboxChangeHandler = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, checked } = event.target;

    setFields((pv) => {
      return {
        ...pv,
        [name]: { value: checked, valid: true },
      };
    });
  };

  const emailValidationHandler = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;
    const lowerValue = value.toLowerCase();
    const valid = Validators.requiredEmail(lowerValue);

    setFields((pv) => {
      return {
        ...pv,
        [name]: { value: lowerValue, valid: valid },
      };
    });
  };

  const requiredTextValidationHandler = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;
    const valid = Validators.required(value);

    setFields((pv) => {
      return {
        ...pv,
        [name]: { value: value, valid: valid },
      };
    });
  };

  const formSubmitHandler = async (event: React.FormEvent) => {
    event.preventDefault();

    if (mode == ManageRegistrationMode.Approval) {
      await approvalFormSubmitHandler(event);
      return;
    }

    if (mode == ManageRegistrationMode.Create) {
      await createFormSubmitHandler(event);
      return;
    }

    if (mode == ManageRegistrationMode.EditApi) {
      await editApiFormSubmitHandler(event);
      return;
    }

    if (mode == ManageRegistrationMode.EditAtlas) {
      await editAtlasFormSubmitHandler(event);
      return;
    }
  };

  const approvalFormSubmitHandler = async (event: React.FormEvent) => {
    if (!formIsValid || dataId === null || dataId === '') {
      return;
    }

    setFormState({ status: FormStatus.Submitting });
    fetch(
      `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_REGISTRATION}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + user!.accessToken,
        },
        body: JSON.stringify({
          id: dataId,
          companyId:
            fields.companyId.value === '' ? null : fields.companyId.value,
          company: fields.company.value === '' ? null : fields.company.value,
          role: fields.role.value === '' ? null : fields.role.value,
          userType: fields.userType.value === '' ? null : fields.userType.value, // Added userType
          apiType: fields.apiType.value === '' ? null : fields.apiType.value,
          apiCompanyIds: fields.apiCompanyIds.value,
          apiCompanies: fields.apiCompanies.value,
          apiData:
            fields.apiData.value.length === 0 ? null : fields.apiData.value,
          approveApi: fields.approveApi.value,
          approveAtlas: fields.approveAtlas.value,
        }),
      }
    )
      .then(async (response) => {
        response.json().then((data) => {
          if (response.status === 200) {
            if (data.matchedCount === 0) {
              setFormState({
                status: FormStatus.Fail,
                error: {
                  code: ErrorCode.notFound,
                  message:
                    'The registration could not be found or has already been approved.',
                },
              });
            } else if (data.modifiedCount === 0) {
              setFormState({
                status: FormStatus.Fail,
                error: {
                  code: ErrorCode.notFound,
                  message:
                    'An error occurred and the registration could not be approved.',
                },
              });
            } else {
              setFormState({ status: FormStatus.Success });
              onSuccess?.();
            }
          } else if (response.status === 404) {
            setFormState({
              status: FormStatus.Fail,
              error: {
                code: ErrorCode.notFound,
                message:
                  'The registration could not be found or has already been approved.',
              },
            });
          } else {
            if (data.code === ErrorCode.userAlreadyExists) {
              setFormState({
                status: FormStatus.Fail,
                error: {
                  code: data.code,
                  message: 'A user already exists with this email.',
                },
              });
            } else {
              setFormState({
                status: FormStatus.Fail,
                error: {
                  code: ErrorCode.unknown,
                  message:
                    'An error occurred and the registration could not be approved.',
                },
              });
            }
          }
        });
      })
      .catch((err) => {
        setFormState({
          status: FormStatus.Error,
          error: {
            code: ErrorCode.unknown,
            message:
              'An error occurred and the registration could not be approved.',
          },
        });
      });
  };

  const createFormSubmitHandler = async (event: React.FormEvent) => {
    if (!formIsValid) {
      return;
    }

    setFormState({ status: FormStatus.Submitting });
    fetch(
      `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_REGISTRATION_ADMIN}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + user!.accessToken,
        },
        body: JSON.stringify({
          regEmail: fields.email.value,
          name: fields.name.value,
          apiRequest: fields.approveApi.value,
          atlasRequest: fields.approveAtlas.value,
          approveApi: fields.approveApi.value,
          approveAtlas: fields.approveAtlas.value,
          companyId: fields.companyId.value,
          company: fields.company.value,
          role: fields.role.value,
          userType: fields.userType.value, // Added userType
          apiType: fields.apiType.value === '' ? null : fields.apiType.value,
          apiData:
            fields.apiData.value.length === 0 ? null : fields.apiData.value,
          apiCompanies: fields.apiCompanies.value,
          apiCompanyIds: fields.apiCompanyIds.value,
        }),
      }
    )
      .then(async (response) => {
        response.json().then((data) => {
          if (response.status === 200) {
            setFormState({ status: FormStatus.Success });
            onSuccess?.();
          } else {
            if (data.code === ErrorCode.userAlreadyExists) {
              setFormState({
                status: FormStatus.Fail,
                error: {
                  code: data.code,
                  message: 'A user with this email already exists.',
                },
              });
            } else if (data.code === ErrorCode.regAlreadyExists) {
              setFormState({
                status: FormStatus.Fail,
                error: {
                  code: data.code,
                  message: 'A registration with this email already exists.',
                },
              });
            } else {
              setFormState({
                status: FormStatus.Fail,
                error: {
                  code: ErrorCode.unknown,
                  message:
                    'An error occurred and the user could not be created and approved.',
                },
              });
            }
          }
        });
      })
      .catch((err) => {
        setFormState({
          status: FormStatus.Error,
          error: {
            code: ErrorCode.unknown,
            message:
              'An error occurred and the user could not be created and approved.',
          },
        });
      });
  };

  const editApiFormSubmitHandler = async (event: React.FormEvent) => {
    if (
      !formIsValid ||
      dataId === null ||
      dataId === '' ||
      profileId === null ||
      profileId === ''
    ) {
      return;
    }

    setFormState({ status: FormStatus.Submitting });
    fetch(
      `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_KEYS}/${dataId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + user!.accessToken,
        },
        body: JSON.stringify({
          id: profileId,
          regId: regDataState.data!.regId,
          name: fields.name.value,
          regEmail: fields.email.value,
          companyIds: fields.apiCompanyIds.value,
          companies: fields.apiCompanies.value,
          apiType: fields.apiType.value === '' ? null : fields.apiType.value,
          apiData:
            fields.apiData.value.length === 0 ? null : fields.apiData.value,
          genNewKey: fields.genNewKey.value,
          userType: fields.userType.value, // Added userType
        }),
      }
    )
      .then(async (response) => {
        response.json().then((data) => {
          if (response.status === 200) {
            if (data.matchedCount === 0) {
              setFormState({
                status: FormStatus.Fail,
                error: {
                  code: ErrorCode.notFound,
                  message: 'The key could not be found.',
                },
              });
            } else if (data.modifiedCount === 0) {
              setFormState({
                status: FormStatus.Fail,
                error: {
                  code: ErrorCode.notFound,
                  message:
                    'An error occurred and the key could not be updated.',
                },
              });
            } else {
              setFormState({ status: FormStatus.Success });
            }
          } else {
            if (data.code === ErrorCode.keyAlreadyExists) {
              setFormState({
                status: FormStatus.Fail,
                error: {
                  code: data.code,
                  message: `A ${fields.apiType.value.toLowerCase()} key for this company already exists.`,
                },
              });
            } else {
              setFormState({
                status: FormStatus.Fail,
                error: {
                  code: ErrorCode.unknown,
                  message:
                    'An error occurred and the key could not be modified.',
                },
              });
            }
          }
        });
      })
      .catch((err) => {
        setFormState({
          status: FormStatus.Error,
          error: {
            code: ErrorCode.unknown,
            message: 'An error occurred and the key could not be modified.',
          },
        });
      });
  };

  const editAtlasFormSubmitHandler = async (event: React.FormEvent) => {
    if (!formIsValid || dataId === null || dataId === '') {
      return;
    }

    setFormState({ status: FormStatus.Submitting });
    fetch(`${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_USER}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + user!.accessToken,
      },
      body: JSON.stringify({
        userId: dataId,
        regId: regDataState.data!.regId,
        oldData: {
          name: regDataState.data!.name,
          regEmail: regDataState.data!.email,
          companyId: regDataState.data!.companyId,
          company: regDataState.data!.company,
          role: regDataState.data!.role,
          userType: regDataState.data!.userType, // Added userType to oldData
        },
        newData: {
          name: fields.name.value,
          regEmail: fields.email.value,
          companyId: fields.companyId.value,
          company: fields.company.value,
          role: fields.role.value,
          userType: fields.userType.value, // Added userType to newData
        },
        sendEmail: true,
      }),
    })
      .then(async (response) => {
        response.json().then((data) => {
          if (response.status === 200) {
            if (data.matchedCount === 0) {
              setFormState({
                status: FormStatus.Fail,
                error: {
                  code: ErrorCode.notFound,
                  message: 'The user could not be found.',
                },
              });
            } else if (data.modifiedCount === 0) {
              setFormState({
                status: FormStatus.Fail,
                error: {
                  code: ErrorCode.notFound,
                  message:
                    'An error occurred and the user could not be updated.',
                },
              });
            } else {
              setFormState({ status: FormStatus.Success });
            }
          } else if (response.status === 404) {
            setFormState({
              status: FormStatus.Fail,
              error: {
                code: ErrorCode.notFound,
                message: 'The user could not be found.',
              },
            });
          } else {
            if (data.code === ErrorCode.userAlreadyExists) {
              setFormState({
                status: FormStatus.Fail,
                error: {
                  code: data.code,
                  message: 'A user with this email already exists.',
                },
              });
            } else if (data.code === ErrorCode.regAlreadyExists) {
              setFormState({
                status: FormStatus.Fail,
                error: {
                  code: data.code,
                  message: 'A registration with this email already exists.',
                },
              });
            } else {
              setFormState({
                status: FormStatus.Fail,
                error: {
                  code: ErrorCode.unknown,
                  message:
                    'An error occurred and the user could not be updated.',
                },
              });
            }
          }
        });
      })
      .catch((err) => {
        setFormState({
          status: FormStatus.Error,
          error: {
            code: ErrorCode.unknown,
            message: 'An error occurred and the user could not be updated.',
          },
        });
      });
  };

  const companyOptions = companyDataState.data!.map((company) => {
    return {
      key: company.companyId,
      label: company.name,
      value: company.companyId,
    };
  });

  const hasPads =
    projectsDataState.data !== undefined && projectsDataState.data!.length > 0;
  let uniquePads: string[] = [];
  if (hasPads) {
    const padNames = projectsDataState.data!.map((project) => project.padName);
    const padsSet = new Set(padNames);
    uniquePads = Array.from(padsSet);
  }

  const { successMessages, dataLoadErrorMessage, dataNotFoundMessage, title } =
    useGetMessages(mode, fields.approveAtlas.value, fields.approveApi.value);

  return (
    <DataForm
      title={title}
      successMessage={successMessages.join(' ')}
      submitErrorMessage={formState.error?.message}
      dataLoadErrorMessage={dataLoadErrorMessage}
      dataLoadNotFoundMessage={dataNotFoundMessage}
      secondaryContainerClass={css.container}
      status={formState.status}
      dataStatuses={[
        regDataState.status,
        companyDataState.status,
        projectsDataState.status,
      ]}
      onSubmit={formSubmitHandler}
    >
      <Visibility visible={mode === ManageRegistrationMode.Approval}>
        <div className={css.userData}>
          <div className={css.userDataItem}>
            <label>Email</label>
            <p>{regDataState.data!.email}</p>
          </div>
          <div className={css.userDataItem}>
            <label>Name</label>
            <p>{regDataState.data!.name}</p>
          </div>
          <div className={css.userDataItem}>
            <label>Requested Atlas Access?</label>
            <p>{regDataState.data!.atlasRequest ? 'Yes' : 'No'}</p>
          </div>
          <div className={css.userDataItem}>
            <label>Requested API Access?</label>
            <p>{regDataState.data!.apiRequest ? 'Yes' : 'No'}</p>
          </div>
        </div>
      </Visibility>
      <Visibility visible={mode !== ManageRegistrationMode.Approval}>
        <TextInput
          label='Email'
          type='email'
          name='email'
          value={fields.email.value}
          valid={fields.email.valid}
          onChange={emailValidationHandler}
        />
        <TextInput
          label='Name'
          type='text'
          name='name'
          value={fields.name.value}
          valid={fields.name.valid}
          onChange={requiredTextValidationHandler}
        />
      </Visibility>
      <Visibility
        visible={
          mode === ManageRegistrationMode.Create ||
          (mode === ManageRegistrationMode.Approval &&
            regDataState.data?.apiRequest === true)
        }
      >
        <CheckboxInput
          label='Approve API Access?'
          name='approveApi'
          value={fields.approveApi.value}
          valid={fields.approveApi.valid}
          onChange={approveApiCheckboxChangeHandler}
          enabled={fields.approveAtlas.value}
        />
        <CheckboxInput
          label='Approve Atlas Access?'
          name='approveAtlas'
          value={fields.approveAtlas.value}
          valid={fields.approveAtlas.valid}
          onChange={approveAtlasCheckboxChangeHandler}
          enabled={fields.approveApi.value}
        />
      </Visibility>
      <Visibility
        visible={
          regDataState.data?.apiRequest === false || fields.approveAtlas.value
        }
      >
        <SelectInput
          placeholder='-- Select --'
          label='Company'
          name='companyId'
          secondaryClass={css.ddl}
          options={companyOptions}
          multiple={false}
          value={fields.companyId.value}
          valid={fields.companyId.valid}
          onChange={companyChangeHandler}
        />
      </Visibility>
      <Visibility
        visible={
          regDataState.data?.apiRequest === false || fields.approveAtlas.value
        }
      >
        <SelectInput
          placeholder='-- Select --'
          label='Role'
          name='role'
          secondaryClass={css.ddl}
          options={[
            { key: 'User', label: 'User', value: 'User' },
            { key: 'Manager', label: 'Manager', value: 'Manager' },
            { key: 'Admin', label: 'Admin', value: 'Admin' },
          ]}
          multiple={false}
          value={fields.role.value}
          valid={fields.role.valid}
          onChange={requiredSelectChangeHandler}
        />
      </Visibility>
      
      {/* User Type Selection */}
      <Visibility
        visible={
          (regDataState.data?.apiRequest === false || fields.approveAtlas.value) && 
          fields.role.value !== ''
        }
      >
        <div className={css.userTypeContainer}>
          <SelectInput
            placeholder='-- Select --'
            label={
              <div className={css.labelWithTooltip}>
                User Type
                <FiInfo 
                  className={css.infoIcon} 
                  onMouseEnter={() => handleTooltipShow(fields.userType.value)}
                  onMouseLeave={handleTooltipHide}
                />
                {showTooltip && (
                  <div className={css.tooltip}>
                    {userTypeTooltips[showTooltip as keyof typeof userTypeTooltips] || ''}
                  </div>
                )}
              </div>
            }
            name='userType'
            secondaryClass={css.ddl}
            options={getUserTypeOptions()}
            multiple={false}
            value={fields.userType.value}
            valid={fields.userType.valid}
            onChange={requiredSelectChangeHandler}
          />
        </div>
      </Visibility>
      
      <Visibility
        visible={
          regDataState.data?.apiRequest === true && fields.approveApi.value
        }
      >
        <SelectInput
          placeholder='-- Select --'
          label='API Access Type'
          name='apiType'
          secondaryClass={css.ddl}
          options={[
            { key: 'Company', label: 'Company', value: 'Company' },
            { key: 'Pad', label: 'Pad', value: 'Pad' },
          ]}
          multiple={false}
          value={fields.apiType.value}
          valid={fields.apiType.valid}
          onChange={apiTypeChangeHandler}
        />
        <SelectInput
          label='API Companies'
          name='apiCompanyIds'
          secondaryClass={css.ddl}
          options={companyOptions}
          multiple={true}
          value={fields.apiCompanyIds.value}
          valid={fields.apiCompanyIds.valid}
          onChange={apiCompanyChangeHandler}
        />
        <Visibility visible={fields.apiType.value === 'Pad' && hasPads}>
          <SelectInput
            label='Pads'
            name='apiData'
            secondaryClass={css.ddl}
            options={uniquePads.map((pad) => {
              return {
                key: pad,
                label: pad,
                value: pad,
              };
            })}
            multiple={true}
            value={fields.apiData.value}
            valid={fields.apiData.valid}
            onChange={requiredSelectChangeHandler}
          />
        </Visibility>
        <Visibility
          visible={
            fields.apiType.value === 'Pad' &&
            fields.apiCompanies.value.length > 0 &&
            !hasPads
          }
        >
          <p className={css.padError}>
            There are no current pads for this company!
          </p>
        </Visibility>
      </Visibility>
      <Visibility visible={mode === ManageRegistrationMode.EditApi}>
        <CheckboxInput
          label='Generate new key?'
          name='genNewKey'
          value={fields.genNewKey.value}
          valid={fields.genNewKey.valid}
          onChange={checkboxChangeHandler}
        />
      </Visibility>
      <div className={css.actions}>
        <button type='submit' disabled={!formIsValid}>
          Approve
        </button>
      </div>
    </DataForm>
  );
};

function useGetDataState(
  mode: ManageRegistrationMode,
  dataId: string | undefined,
  setFields: (value: React.SetStateAction<ManageRegistrationFormFields>) => void
) {
  const dataSet = dataId !== undefined && dataId !== '';
  const initialData: ManageRegistrationData = {
    email: '',
    name: '',
    companyId: '',
    company: '',
    apiRequest: mode === ManageRegistrationMode.Create ? true : false,
    atlasRequest: mode === ManageRegistrationMode.Create ? true : false,
    role: '',
    userType: '', // Added userType
    apiType: '',
    apiData: [],
    apiCompanyIds: [],
    apiCompanies: [],
  };
  const onComplete: (
    data: ManageRegistrationData | undefined,
    status: DataStatus,
    error?: DataError
  ) => void = (data, status, error) => {
    if (status === DataStatus.Ready && data !== undefined) {
      const apiRequest = data.apiRequest;
      const atlasRequest = data.atlasRequest;

      setFields((pv) => {
        // Determine userType value and validity
        let userTypeValue = data.userType || '';
        // If no userType is provided but role exists, default userType to role
        if (!userTypeValue && data.role) {
          userTypeValue = data.role;
        }
        const userTypeValid = Validators.required(userTypeValue) || 
          mode === ManageRegistrationMode.EditApi || 
          !atlasRequest;

        return {
          ...pv,
          email: {
            value: data.email,
            valid: Validators.requiredEmail(data.email),
          },
          name: { value: data.name, valid: Validators.required(data.name) },
          companyId: {
            value: data.companyId,
            valid: Validators.required(data.companyId),
          },
          company: {
            value: data.company,
            valid: Validators.required(data.company),
          },
          approveApi: { value: apiRequest, valid: true },
          approveAtlas: { value: atlasRequest, valid: true },
          role: {
            value: data.role,
            valid:
              mode === ManageRegistrationMode.EditApi ||
              Validators.required(data.role),
          },
          userType: { // Added userType
            value: userTypeValue,
            valid: userTypeValid,
          },
          apiType: {
            value: data.apiType,
            valid: !apiRequest || Validators.required(data.apiType),
          },
          apiData: {
            value: data.apiData,
            valid: !apiRequest || data.apiData.length > 0,
          },
          apiCompanyIds: {
            value: data.apiCompanyIds,
            valid: !apiRequest || data.apiCompanyIds.length > 0,
          },
          apiCompanies: {
            value: data.apiCompanies,
            valid: !apiRequest || data.apiCompanies.length > 0,
          },
        };
      });
    }
  };

  const regDataState = useData<Registration, ManageRegistrationData>({
    endpoint: `${process.env.REACT_APP_API_REGISTRATION}/${dataId}?fa=true`,
    initialData: initialData,
    initialStatus:
      mode === ManageRegistrationMode.Create
        ? DataStatus.Ready
        : DataStatus.Loading,
    loadWhen:
      (mode === ManageRegistrationMode.Approval ||
        mode === ManageRegistrationMode.Create) &&
      dataSet,
    modifyData: (data): ManageRegistrationData => {
      return {
        email: data!.regEmail,
        name: data!.name,
        companyId: '',
        company: '',
        apiRequest: data!.apiRequest,
        atlasRequest: data!.atlasRequest,
        role: '',
        userType: data!.userType || '', // Added userType
        apiType: '',
        apiData: [],
        apiCompanyIds: [],
        apiCompanies: [],
      };
    },
    onComplete: onComplete,
  });

  const userDataState = useData<
    UserProfileSearchResult,
    ManageRegistrationData
  >({
    endpoint: `${process.env.REACT_APP_API_USER}/${dataId}`,
    initialData: initialData,
    initialStatus: DataStatus.Loading,
    loadWhen: mode === ManageRegistrationMode.EditAtlas && dataSet,
    modifyData: (data): ManageRegistrationData => {
      return {
        regId: data!.regId,
        email: data!.email,
        name: data!.name,
        companyId: data!.companyId,
        company: data!.company,
        apiRequest: false,
        atlasRequest: true,
        role: data!.role,
        userType: data!.userType || data!.role || '', // Added userType with fallback to role
        apiType: '',
        apiData: [],
        apiCompanyIds: [],
        apiCompanies: [],
      };
    },
    onComplete: onComplete,
  });

  const apiDataState = useData<KeyProfile, ManageRegistrationData>({
    endpoint: `${process.env.REACT_APP_API_KEYS}/${dataId}`,
    initialData: initialData,
    initialStatus: DataStatus.Loading,
    loadWhen: mode === ManageRegistrationMode.EditApi && dataSet,
    modifyData: (data): ManageRegistrationData => {
      return {
        regId: data!.regId,
        email: data!.email,
        name: data!.name,
        companyId: data!.companyIds[0],
        company: data!.companies[0],
        apiRequest: true,
        atlasRequest: false,
        role: '',
        userType: data!.userType || '', // Added userType
        apiType: data!.pads === null ? 'Company' : 'Pad',
        apiData: data!.pads === null ? data!.companies : data!.pads,
        apiCompanyIds: data!.companyIds,
        apiCompanies: data!.companies,
      };
    },
    onComplete: onComplete,
  });

  if (mode === ManageRegistrationMode.EditAtlas) {
    return userDataState;
  } else if (mode === ManageRegistrationMode.EditApi) {
    return apiDataState;
  }

  return regDataState;
}

function useGetMessages(
  mode: ManageRegistrationMode,
  approveAtlas: boolean,
  approveApi: boolean
) {
  const successMessages = [];
  let dataLoadErrorMessage = '';
  let dataNotFoundMessage = '';
  let title = 'Create Registration';

  if (approveAtlas) {
    if (mode === ManageRegistrationMode.Approval) {
      successMessages.push(
        'The registration has been approved and the user can now create their account.'
      );
    } else if (mode === ManageRegistrationMode.Create) {
      successMessages.push(
        'The registration has been created and approved.',
        'The user can now create their account.'
      );
    } else if (mode === ManageRegistrationMode.EditAtlas) {
      successMessages.push('The user profile has been modified.');
    }
  }

  if (approveApi) {
    if (
      mode === ManageRegistrationMode.Approval ||
      mode === ManageRegistrationMode.Create
    ) {
      successMessages.push(
        'A Liberty Link API key has been created and sent to the user.'
      );
    } else if (mode === ManageRegistrationMode.EditApi) {
      successMessages.push(
        'The Liberty Link API key has been modified.',
        'If a new key was generated, it has been sent to the user.'
      );
    }
  }

  if (mode === ManageRegistrationMode.Approval) {
    dataLoadErrorMessage =
      'An error occurred and the registration can not be processed.';
    dataNotFoundMessage =
      'The registration could not be found or has already been approved.';
    title = 'Approve Registration';
  } else if (mode === ManageRegistrationMode.EditAtlas) {
    dataLoadErrorMessage =
      'An error occurred and the user profile can not be processed.';
    dataNotFoundMessage = 'The user profile could not be found.';
    title = 'Manage User';
  } else if (mode === ManageRegistrationMode.EditApi) {
    dataLoadErrorMessage =
      'An error occurred and the api key can not be processed.';
    dataNotFoundMessage = 'The api key could not be found.';
    title = 'Manage API Key';
  }

  return { successMessages, dataLoadErrorMessage, dataNotFoundMessage, title };
}

export default ManageRegistrationForm;